/**
 * Le registre national du parent, demandé pour l'attestation fiscale.
 *
 * ## Facultatif, mais pas discret
 *
 * Sans ce numéro, pas d'attestation fiscale pour les frais de garde : la
 * famille le découvre en mai, quand elle remplit sa déclaration. Le champ
 * n'est pas obligatoire — un parent qui n'a pas sa carte sous la main doit
 * pouvoir inscrire son enfant — mais il est présenté pour ce qu'il est : la
 * seule chose qui manque encore pour recevoir l'attestation.
 *
 * ## La date de naissance aide et vérifie
 *
 * Elle fournit les six premiers chiffres du numéro (proposés à l'entrée de
 * la première case) et sert à vérifier que le numéro saisi est bien celui du
 * parent, pas celui de l'enfant recopié par réflexe. Elle n'est pas stockée.
 * Le numéro lu est affiché en clair (« né·e le 15/08/1986 ») pour qu'une
 * faute de frappe se voie avant d'être enregistrée.
 *
 * Même bloc sur `/bienvenue` et sur `/profile` : une seule règle,
 * `erreursCoordonnees`, les vérifie aux deux endroits.
 */

import { AlertCircle, CheckCircle2 } from 'lucide-react';
import clsx from 'clsx';
import BelgianNRNInput from '../common/BelgianNRNInput';
import { Bloc, Pourquoi, Saisie } from '../forms/kid/champs';
import { dateLisible, lireNRN, prefixeNRN } from '../../lib/registreNational';
import { nrnParentRetenu } from '../../lib/coordonnees';

const RegistreNationalParent = ({
  dateNaissance,
  onDateNaissance,
  nnational,
  onNnational,
  erreurs,
}: {
  dateNaissance: string;
  onDateNaissance: (v: string) => void;
  nnational: string;
  onNnational: (v: string) => void;
  erreurs: { date_naissance?: string; nnational?: string };
}) => {
  const retenu = nrnParentRetenu({ nnational, date_naissance: dateNaissance });
  const lecture = retenu && !erreurs.nnational ? lireNRN(retenu) : null;
  const dateLue = lecture?.ok ? lecture.lu.dateNaissance : null;

  return (
    <Bloc
      titre="Pour votre attestation fiscale"
      intro="Chaque année, l’attestation fiscale des frais de garde demande le numéro de registre national du parent. Sans lui, nous ne pouvons pas l’établir. Il est au dos de votre carte d’identité."
    >
      <div className="grid gap-5 md:grid-cols-2">
        <Saisie
          label="Votre date de naissance"
          type="date"
          valeur={dateNaissance}
          onChange={onDateNaissance}
          erreur={erreurs.date_naissance}
          aide="Elle sert seulement à vérifier le numéro ; nous ne la conservons pas."
          autoComplete="bday"
        />

        <div>
          <label className="mb-1 flex flex-wrap items-center gap-2 text-sm font-medium text-gray-700">
            Votre numéro de registre national
            {!retenu ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 ring-1 ring-inset ring-amber-200">
                <AlertCircle className="h-3 w-3" aria-hidden="true" />
                À compléter
              </span>
            ) : (
              <span className="text-xs font-normal text-gray-400">facultatif</span>
            )}
          </label>
          <BelgianNRNInput
            value={nnational}
            onChange={onNnational}
            error={retenu ? erreurs.nnational : undefined}
            prefixe={prefixeNRN(dateNaissance)}
          />
          {erreurs.nnational && retenu && (
            <button
              type="button"
              onClick={() => onNnational('')}
              className="mt-1 text-sm font-medium text-primary-600 underline hover:text-primary-700"
            >
              Continuer sans ce numéro (vous pourrez l’ajouter plus tard)
            </button>
          )}
          {dateLue && (
            <p className="mt-1 inline-flex items-center gap-1 text-xs text-green-700">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
              Ce numéro correspond à une personne née le {dateLisible(dateLue)}.
            </p>
          )}
          {!retenu && (
            <p className={clsx('mt-1 text-xs', 'text-amber-700')}>
              Sans lui, pas d’attestation fiscale. Il n’empêche pas d’inscrire vos enfants&nbsp;: vous
              pourrez le renseigner plus tard.
            </p>
          )}
          <Pourquoi>
            Il ne sert qu’aux attestations fiscales et aux obligations qui y sont directement
            liées&nbsp;: déclarations à l’ONE et aux communes partenaires. Il n’apparaît sur
            aucune liste de présence.
          </Pourquoi>
        </div>
      </div>
    </Bloc>
  );
};

export default RegistreNationalParent;
